import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiMutation, useApiQuery } from '@/shared/hooks';
import { apiClient, API_BASE_URL } from '@/shared/api/client';
import { endpoints } from '@/shared/api/endpoints';
import { getEndpoint, getMutationMethod } from '@/shared/utils/getUrl';
import { toast } from '@/shared/store/useToastStore';
import { useAppStore } from '@/shared/store/useAppStore';
import type {
  UserData,
  UpdateUserRequest,
  ChangePasswordRequest,
  UserSessionsResponse,
  TwoFactorStatusResponse,
  TwoFactorSetupResponse,
} from './types';

export const useUploadAvatarMutation = () => {
  const queryClient = useQueryClient();
  const setUser = useAppStore((s) => s.setUser);

  return useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const url = `${API_BASE_URL}${getEndpoint(endpoints.users.avatar)}`;
      const res = await fetch(url, {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });
      if (!res.ok) {
        throw new Error(`avatar upload failed: ${res.status}`);
      }
      return (await res.json()) as UserData;
    },
    onSuccess: (userData) => {
      setUser(userData);
      toast.success('Avatar updated');
      void queryClient.invalidateQueries({ queryKey: ['user', 'me'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: () => {
      toast.error('Failed to upload avatar');
    },
  });
};

export const useUpdateUserMutation = () => {
  return useApiMutation<{ user: UserData }, { id: string } & UpdateUserRequest>(
    getEndpoint(endpoints.users.update, ':id'),
    getMutationMethod(endpoints.users.update),
    {
      getUrl: ({ id }) => getEndpoint(endpoints.users.update, id),
      getPayload: ({ id: _id, ...rest }) => rest,
      invalidateQueries: [['user', 'me']],
      onSuccess: () => {
        toast.success('Profile updated successfully');
      },
      onError: (error) => {
        console.error('Failed to update profile', error);
        toast.error('Failed to update profile');
      },
    }
  );
};

export const useChangePasswordMutation = () => {
  return useApiMutation<void, ChangePasswordRequest>(
    getEndpoint(endpoints.users.changePassword),
    getMutationMethod(endpoints.users.changePassword),
    {
      onSuccess: () => {
        toast.success('Password changed successfully');
      },
      onError: (error) => {
        console.error('Failed to change password', error);
        toast.error('Failed to change password');
      },
    }
  );
};

export const useForceChangePasswordMutation = (options?: {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}) => {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, ChangePasswordRequest>(
    getEndpoint(endpoints.users.changePassword),
    getMutationMethod(endpoints.users.changePassword),
    {
      ...options,
      onSuccess: () => {
        queryClient.setQueryData<UserData>(['user', 'me'], (user) =>
          user ? { ...user, requires_password_change: false } : user
        );
        const user = useAppStore.getState().user;
        if (user) {
          useAppStore.getState().setUser({ ...user, requires_password_change: false });
        }
        useAppStore.getState().setFirstTimeLogin(false);
        options?.onSuccess?.();
      },
    }
  );
};

export const useLogoutMutation = (options?: {
  onSuccess?: () => void;
  onError?: () => void;
}) => {
  return useApiMutation<unknown, void>(
    getEndpoint(endpoints.logout),
    getMutationMethod(endpoints.logout),
    options
  );
};

// Sessions Hooks

export const useUserSessionsQuery = () => {
  return useApiQuery<UserSessionsResponse>(
    ['user', 'sessions'],
    getEndpoint(endpoints.users.sessions.list),
    {
      refetchOnWindowFocus: false,
    }
  );
};

export const useTerminateSessionMutation = () => {
  return useApiMutation<void, { id: string }>(
    getEndpoint(endpoints.users.sessions.terminate, ':id'),
    getMutationMethod(endpoints.users.sessions.terminate),
    {
      getUrl: ({ id }) => getEndpoint(endpoints.users.sessions.terminate, id),
      invalidateQueries: [['user', 'sessions']],
      onSuccess: () => {
        toast.success('Session terminated successfully');
      },
      onError: (error) => {
        console.error('Failed to terminate session', error);
        toast.error('Failed to terminate session');
      },
    }
  );
};

export const useTerminateAllSessionsMutation = () => {
  return useApiMutation<void, void>(
    getEndpoint(endpoints.users.sessions.terminateAll),
    getMutationMethod(endpoints.users.sessions.terminateAll),
    {
      invalidateQueries: [['user', 'sessions']],
      onSuccess: () => {
        toast.success('All sessions terminated');
      },
      onError: (error) => {
        console.error('Failed to terminate all sessions', error);
        toast.error('Failed to terminate all sessions');
      },
    }
  );
};

// Two Factor Hooks

export const useTwoFactorStatusQuery = () => {
  return useQuery<TwoFactorStatusResponse>({
    queryKey: ['user', '2fa', 'status'],
    queryFn: async () => {
      // Status endpoint uses SuccessResponse wrapper: { code, message, data: {...} }
      const res = await apiClient.get<{ data: TwoFactorStatusResponse }>(
        getEndpoint(endpoints.twoFactor.status)
      );
      return res.data.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useSetupTwoFactorMutation = () => {
  return useApiMutation<TwoFactorSetupResponse, { password: string }>(
    getEndpoint(endpoints.twoFactor.setup),
    getMutationMethod(endpoints.twoFactor.setup),
    {
      onError: (error) => {
        console.error('Failed to setup 2FA', error);
        toast.error('Failed to setup 2FA');
      },
    }
  );
};

export const useVerifyTwoFactorSetupMutation = () => {
  return useApiMutation<{ codes: string[] }, { code: string }>(
    getEndpoint(endpoints.twoFactor.verifySetup),
    getMutationMethod(endpoints.twoFactor.verifySetup),
    {
      invalidateQueries: [['user', '2fa', 'status']],
      onSuccess: () => {
        toast.success('2FA enabled successfully');
      },
      onError: (error) => {
        console.error('Failed to verify 2FA setup', error);
        toast.error('Invalid code');
      },
    }
  );
};

export const useDisableTwoFactorMutation = () => {
  return useApiMutation<void, { code: string; password: string }>(
    getEndpoint(endpoints.twoFactor.disable),
    getMutationMethod(endpoints.twoFactor.disable),
    {
      invalidateQueries: [['user', '2fa', 'status']],
      onSuccess: () => {
        toast.success('2FA disabled successfully');
      },
      onError: (error) => {
        console.error('Failed to disable 2FA', error);
        toast.error('Failed to disable 2FA');
      },
    }
  );
};

export const useRegenerateBackupCodesMutation = () => {
  return useApiMutation<{ codes: string[] }, { password: string }>(
    getEndpoint(endpoints.twoFactor.regenerateBackupCodes),
    getMutationMethod(endpoints.twoFactor.regenerateBackupCodes),
    {
      invalidateQueries: [['user', '2fa', 'status']],
      onSuccess: () => {
        toast.success('Backup codes regenerated successfully');
      },
      onError: (error) => {
        console.error('Failed to regenerate backup codes', error);
        toast.error('Failed to regenerate backup codes');
      },
    }
  );
};
